import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { 
  EventBusy, 
  PersonOff, 
  SearchOff, 
  AssignmentLate,
  DirectionsCar,
  EmojiEvents
} from '@mui/icons-material';

interface EmptyStateProps {
  type: 'races' | 'drivers' | 'search' | 'standings' | 'results' | 'generic';
  message?: string;
  actionLabel?: string;
  onAction?: () => void;
}

const getEmptyStateConfig = (type: string) => {
  switch (type) {
    case 'races':
      return {
        icon: EventBusy,
        title: 'No Races Found',
        message: 'There are no races scheduled for the selected criteria.',
        actionLabel: 'Create New Race'
      };
    case 'drivers':
      return {
        icon: PersonOff,
        title: 'No Drivers Found',
        message: 'No drivers match your current filters.',
        actionLabel: 'Add New Driver'
      };
    case 'search':
      return {
        icon: SearchOff,
        title: 'No Results Found',
        message: 'Try adjusting your search terms or filters.',
        actionLabel: 'Clear Filters'
      };
    case 'standings':
      return {
        icon: EmojiEvents,
        title: 'No Standings Available',
        message: 'Standings will appear once races have been completed.',
        actionLabel: 'View Race Schedule'
      };
    case 'results':
      return {
        icon: DirectionsCar,
        title: 'No Race Results',
        message: 'Race results will appear here once entered.',
        actionLabel: 'Enter Results'
      };
    default:
      return {
        icon: AssignmentLate,
        title: 'No Data Available',
        message: 'No data to display at this time.',
        actionLabel: null
      };
  }
};

export const EmptyState: React.FC<EmptyStateProps> = ({ 
  type, 
  message: customMessage, 
  actionLabel: customActionLabel,
  onAction 
}) => {
  const config = getEmptyStateConfig(type);
  const Icon = config.icon;
  
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
        px: 3,
        textAlign: 'center'
      }}
    >
      <Icon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
      <Typography variant="h6" gutterBottom color="text.secondary">
        {config.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
        {customMessage || config.message}
      </Typography>
      {(customActionLabel || config.actionLabel) && onAction && (
        <Button 
          variant="contained" 
          onClick={onAction}
          size="small"
        >
          {customActionLabel || config.actionLabel}
        </Button>
      )}
    </Box>
  );
};